// Dependencies
import * as Yup from "yup";
import { FC, useRef } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Form, Formik } from "formik";
import { useQuery } from "react-query";
import { useSnackbar } from "notistack";

// Components
import { MenuItem } from "@mui/material";
import { If } from "jsx-control-statements";
import Switch from "~/components/Common/Switch";
import FormItem from "../../Layout/LayoutForm/FormItem";
import Button, { WarningButton } from "~/components/Common/Button/Button";

// api
import { getCurrencyUnits } from "~/services/wallet";

// Types
import { QUERY_KEY } from "~/enums";
import { TWalletDetailData, TQueryError } from "~/types/types";

// Utils
import { convertDayjsUTCtoBackendUTC, handleCancelForm } from "~/utils";

// Styles
import {
  StyledUnsaved,
  StyledFormInput,
  StyledFormSelect,
  StyledDatePicker,
  StyledFormAction,
  StyledCurrencyInput,
} from "../../Layout/LayoutForm/Form.styled";
import {
  StyledInfoDetail,
  StyledInfoContent,
} from "~/components/Layout/LayoutInfo/LayoutInfo.styled";

interface WalletFormProps {
  type: string;
  onCancel: Function;
  onSubmit: Function;
  initialData?: TWalletDetailData;
}

type BasicWalletValues = {
  name: string;
  currencyId: string;
  excludeFromTotal: boolean;
  initialBalance: string;
};

type GoldWalletValues = {
  name: string;
  currencyId: string;
  excludeFromTotal: boolean;
  goalValue: string;
  startingAmount: string;
  endingDate: Dayjs;
  enableNotify: boolean;
};

const WalletForm: FC<WalletFormProps> = ({
  type,
  onCancel,
  onSubmit,
  initialData,
}) => {
  const formRef = useRef<any>(null);
  const { enqueueSnackbar } = useSnackbar();

  const currencyListQuery = useQuery({
    queryKey: QUERY_KEY.CURRENCY.CURRENCY_LIST,
    queryFn: getCurrencyUnits,
    onError(error: TQueryError) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  // Methods
  const handleSubmitForm = (values: BasicWalletValues | GoldWalletValues) => {
    let editedValues: any = { ...values };
    if ("endingDate" in values) {
      editedValues.endingDate = convertDayjsUTCtoBackendUTC(values.endingDate);
    }

    onSubmit({
      ...editedValues,
      type,
    });
  };

  const currencyList = currencyListQuery.data?.map(
    (item: { currencyID: string; description: string }) => (
      <MenuItem value={item.currencyID} key={item.currencyID}>
        {item.currencyID} ({item.description})
      </MenuItem>
    )
  );

  const BasicWalletForm = (
    <Formik
      initialValues={{
        name: "",
        currencyId: "",
        excludeFromTotal: false,
        initialBalance: "0",
        ...initialData,
      }}
      // validate form values
      validationSchema={Yup.object({
        name: Yup.string().required("name is required"),
        currencyId: Yup.string().required("Currency is required"),
      })}
      // handle submit form
      onSubmit={handleSubmitForm}
      innerRef={formRef}
    >
      {({ values, errors, touched, handleChange, handleBlur, dirty }) => (
        <Form>
          <FormItem
            label={"Name"}
            htmlFor="name"
            hasError={!!(errors.name && touched.name)}
            error={errors.name}
            required
            formItem={
              <StyledFormInput
                name={"name"}
                id={"name"}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={"Enter wallet name"}
                variant={"standard"}
              />
            }
          />
          <FormItem
            label={"Currency"}
            htmlFor="currencyId"
            hasError={!!(errors.currencyId && touched.currencyId)}
            error={errors.currencyId}
            required
            formItem={
              <StyledFormSelect
                name={"currencyId"}
                id={"currencyId"}
                labelId="currencyId"
                value={values.currencyId}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={!!initialData}
              >
                {currencyList}
              </StyledFormSelect>
            }
          />
          <FormItem
            label={"Exclude From Total"}
            htmlFor="excludeFromTotal"
            formItem={
              <Switch
                name={"excludeFromTotal"}
                id={"excludeFromTotal"}
                checked={values.excludeFromTotal}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            }
          />
          <FormItem
            label={"Initial Balance"}
            htmlFor="initialBalance"
            formItem={
              <StyledCurrencyInput
                name={"initialBalance"}
                id={"initialBalance"}
                value={values.initialBalance}
                placeholder={"Enter initial balance"}
                decimalsLimit={2}
                onValueChange={(value) => {
                  handleChange({ target: { name: "initialBalance", value } });
                }}
              />
            }
          />
          <If condition={dirty}>
            <StyledUnsaved>Your form has unsaved fields!</StyledUnsaved>
          </If>
          <StyledFormAction>
            <WarningButton onClick={() => handleCancelForm(formRef, onCancel)}>
              Cancel
            </WarningButton>
            <Button type={"submit"} variant={"contained"}>
              Save
            </Button>
          </StyledFormAction>
        </Form>
      )}
    </Formik>
  );

  const GoldWalletForm = (
    <Formik
      initialValues={{
        name: "",
        currencyId: "",
        excludeFromTotal: false,
        goalValue: "0",
        startingAmount: "0",
        enableNotify: false,
        ...initialData,
        endingDate: dayjs(initialData?.endingDate),
      }}
      // validate form values
      validationSchema={Yup.object({
        name: Yup.string().required("name is required"),
        currencyId: Yup.string().required("Currency is required"),
        goalValue: Yup.number()
          .required("Goal value is required")
          .moreThan(0, "Goal value must be greater than 0"),
        endingDate: Yup.string().required("Ending date is required"),
      })}
      // handle submit form
      onSubmit={handleSubmitForm}
      innerRef={formRef}
    >
      {({ values, errors, touched, handleChange, handleBlur, dirty }) => (
        <Form>
          <FormItem
            label={"Name"}
            htmlFor="name"
            hasError={!!(errors.name && touched.name)}
            error={errors.name}
            required
            formItem={
              <StyledFormInput
                name={"name"}
                id={"name"}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={"Enter wallet name"}
                variant={"standard"}
              />
            }
          />
          <FormItem
            label={"Currency"}
            htmlFor="currencyId"
            hasError={!!(errors.currencyId && touched.currencyId)}
            error={errors.currencyId}
            required
            formItem={
              <StyledFormSelect
                name={"currencyId"}
                id={"currencyId"}
                labelId="currencyId"
                value={values.currencyId}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={!!initialData}
              >
                {currencyList}
              </StyledFormSelect>
            }
          />
          <FormItem
            label={"Exclude From Total"}
            htmlFor="excludeFromTotal"
            formItem={
              <Switch
                name={"excludeFromTotal"}
                id={"excludeFromTotal"}
                checked={values.excludeFromTotal}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            }
          />
          <FormItem
            label={"Goal Value"}
            htmlFor="goalValue"
            hasError={!!(errors.goalValue && touched.goalValue)}
            error={errors.goalValue}
            required
            formItem={
              <StyledCurrencyInput
                name={"goalValue"}
                id={"goalValue"}
                value={values.goalValue}
                placeholder={"Enter goal value"}
                decimalsLimit={2}
                onValueChange={(value) => {
                  handleChange({ target: { name: "goalValue", value } });
                }}
              />
            }
          />
          <FormItem
            label={"Starting Amount"}
            htmlFor="startingAmount"
            formItem={
              <StyledCurrencyInput
                name={"startingAmount"}
                id={"startingAmount"}
                value={values.startingAmount}
                placeholder={"Enter starting amount"}
                decimalsLimit={2}
                onValueChange={(value) => {
                  handleChange({ target: { name: "startingAmount", value } });
                }}
              />
            }
          />
          <FormItem
            label={"Ending Date"}
            htmlFor="endingDate"
            hasError={!!(errors.endingDate && touched.endingDate)}
            error={errors.endingDate as string}
            required
            formItem={
              <StyledDatePicker
                value={values.endingDate}
                format="DD/MM/YYYY"
                onChange={(value) => {
                  const date = value as Dayjs;
                  handleChange({
                    target: { value: date, name: "endingDate" },
                  });
                }}
              />
            }
          />
          <FormItem
            label={"Enable Notification"}
            htmlFor="enableNotify"
            formItem={
              <Switch
                name={"enableNotify"}
                id={"enableNotify"}
                value={values.enableNotify}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            }
          />
          <If condition={dirty}>
            <StyledUnsaved>Your form has unsaved fields!</StyledUnsaved>
          </If>
          <StyledFormAction>
            <WarningButton onClick={() => handleCancelForm(formRef, onCancel)}>
              Cancel
            </WarningButton>
            <Button type={"submit"} variant={"contained"}>
              Save
            </Button>
          </StyledFormAction>
        </Form>
      )}
    </Formik>
  );

  return (
    <StyledInfoContent>
      <StyledInfoDetail>
        <If condition={type === "Basic"}>{BasicWalletForm}</If>
        <If condition={type === "Goal"}>{GoldWalletForm}</If>
      </StyledInfoDetail>
    </StyledInfoContent>
  );
};

export default WalletForm;
